import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit3, Trash2, RefreshCw, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';
import { formatProgrammeLabel, formatCategoryLabel, matchesProgrammeSearch } from '../../lib/programmeLabel';
import { FlowCatalogEntry, FlowSchedule } from '../../types/flow';
import { formatDate } from './FlowHelpers';

interface FlowSchedulesTabProps {
  flowCatalog: FlowCatalogEntry[];
}

// =====================
// Schedule Helpers
// =====================

const DAY_LABELS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mo–So for display

const INTERVAL_OPTIONS = [
  { value: 15, label: '15 Minuten' },
  { value: 30, label: '30 Minuten' },
  { value: 60, label: '1 Stunde' },
  { value: 120, label: '2 Stunden' },
  { value: 240, label: '4 Stunden' },
  { value: 360, label: '6 Stunden' },
  { value: 720, label: '12 Stunden' },
  { value: 1440, label: '24 Stunden' },
];

const activeHoursToPause = (from: number, to: number): number[] | null => {
  if (from === to) return null;
  const active = new Set<number>();
  let h = from;
  while (h !== to) { active.add(h); h = (h + 1) % 24; }
  return Array.from({ length: 24 }, (_, i) => i).filter(i => !active.has(i));
};

const pauseToActiveHours = (pause?: number[] | null): { from: number; to: number } => {
  if (!pause || pause.length === 0) return { from: 0, to: 0 };
  const pauseSet = new Set(pause);
  for (let i = 0; i < 24; i++) {
    if (!pauseSet.has(i) && pauseSet.has((i + 23) % 24)) {
      let j = i;
      while (!pauseSet.has(j)) j = (j + 1) % 24;
      return { from: i, to: j };
    }
  }
  return { from: 0, to: 0 };
};

const activeDaysToPause = (days: number[]): number[] | null => {
  if (days.length >= 7) return null;
  const pause = [0, 1, 2, 3, 4, 5, 6].filter(d => !days.includes(d));
  return pause.length > 0 ? pause : null;
};

const pauseToActiveDays = (pause?: number[] | null): number[] => {
  if (!pause || pause.length === 0) return [0, 1, 2, 3, 4, 5, 6];
  return [0, 1, 2, 3, 4, 5, 6].filter(d => !pause.includes(d));
};

const formatActiveHours = (pause?: number[] | null): string => {
  if (!pause || pause.length === 0) return 'Ganztägig';
  const { from, to } = pauseToActiveHours(pause);
  if (from === to) return 'Ganztägig';
  return `${String(from).padStart(2, '0')}:00\u2013${String(to).padStart(2, '0')}:00`;
};

const formatActiveDays = (pause?: number[] | null): string => {
  if (!pause || pause.length === 0) return 'Täglich';
  const active = pauseToActiveDays(pause);
  if (active.length === 7) return 'Täglich';
  if (active.length === 0) return 'Nie';
  const ordered = DAY_ORDER.filter(d => active.includes(d));
  const positions = ordered.map(d => DAY_ORDER.indexOf(d));
  const isContiguous = positions.length >= 2 && positions.every((p, i) => i === 0 || p === positions[i - 1] + 1);
  const labels = ordered.map(d => DAY_LABELS[d]);
  if (isContiguous) return `${labels[0]}\u2013${labels[labels.length - 1]}`;
  return labels.join(', ');
};

const formatInterval = (minutes: number): string => {
  const opt = INTERVAL_OPTIONS.find(o => o.value === minutes);
  if (opt) return opt.label;
  if (minutes < 60) return `${minutes} Min`;
  if (minutes % 60 === 0) return `${minutes / 60} Std`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const formatScheduleType = (schedule: FlowSchedule): string => {
  if (schedule.scheduleMode === 'fixedTimes' && schedule.customTimes?.length) {
    const times = [...schedule.customTimes].sort();
    if (times.length <= 3) return times.join(', ');
    return `${times.length}\u00d7 t\u00e4glich`;
  }
  return formatInterval(schedule.intervalMinutes);
};

// =====================
// Schedule Modal
// =====================

interface ScheduleModalProps {
  schedule: FlowSchedule | null;
  catalog: FlowCatalogEntry[];
  existingFlowIds: string[];
  onClose: () => void;
  onSave: (flowId: string, data: any) => Promise<void>;
}

const ScheduleModal: React.FC<ScheduleModalProps> = ({ schedule, catalog, existingFlowIds, onClose, onSave }) => {
  const [flowId, setFlowId] = useState(schedule?.flowId || '');
  const [scheduleMode, setScheduleMode] = useState<'interval' | 'fixedTimes'>(schedule?.scheduleMode || 'interval');
  const [intervalMinutes, setIntervalMinutes] = useState(schedule?.intervalMinutes || 60);
  const [customTimes, setCustomTimes] = useState<string[]>(schedule?.customTimes || ['08:00']);
  const [enabled, setEnabled] = useState(schedule?.enabled ?? true);
  const [activeFrom, setActiveFrom] = useState(() => pauseToActiveHours(schedule?.pauseHours).from);
  const [activeTo, setActiveTo] = useState(() => pauseToActiveHours(schedule?.pauseHours).to);
  const [activeDays, setActiveDays] = useState<number[]>(() => pauseToActiveDays(schedule?.pauseDays));
  const [params, setParams] = useState<Record<string, any>>(schedule?.params || {});
  const [saving, setSaving] = useState(false);

  const isCreate = !schedule;
  const availableFlows = isCreate
    ? catalog.filter(f => !existingFlowIds.includes(f.id))
    : catalog;

  const selectedFlow = catalog.find(f => f.id === flowId);

  const paramDefs: [string, any][] = selectedFlow?.parameters
    ? (Array.isArray(selectedFlow.parameters)
        ? selectedFlow.parameters.map((def: any) => [def.key, def])
        : Object.entries(selectedFlow.parameters))
    : [];

  const handleSave = async () => {
    if (!flowId) { toast.error('Bitte einen Flow auswählen'); return; }
    if (scheduleMode === 'interval' && intervalMinutes < 1) { toast.error('Intervall muss mindestens 1 Minute sein'); return; }
    if (scheduleMode === 'fixedTimes' && customTimes.length < 1) { toast.error('Mindestens eine Uhrzeit angeben'); return; }
    setSaving(true);
    try {
      await onSave(flowId, {
        scheduleMode,
        ...(scheduleMode === 'interval'
          ? { intervalMinutes, pauseHours: activeHoursToPause(activeFrom, activeTo) }
          : { intervalMinutes: 60, customTimes: [...customTimes].sort(), pauseHours: null }),
        pauseDays: activeDaysToPause(activeDays),
        params,
        enabled,
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day: number) => {
    setActiveDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const updateTimeCount = (count: number) => {
    const clamped = Math.max(1, Math.min(24, count));
    setCustomTimes(prev => {
      if (clamped > prev.length) {
        return [...prev, ...Array(clamped - prev.length).fill('08:00')];
      }
      return prev.slice(0, clamped);
    });
  };

  const updateTime = (index: number, value: string) => {
    setCustomTimes(prev => prev.map((t, i) => i === index ? value : t));
  };

  const hourOptions = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-xl p-8 w-[520px] max-w-[90vw] max-h-[85vh] overflow-y-auto shadow-xl">
        <h3 className="mb-5 text-base font-semibold text-[#1A1A1A]">
          {isCreate ? 'Schedule erstellen' : `Schedule bearbeiten: ${schedule?.flowName}`}
        </h3>

        {isCreate && (
          <div className="mb-4">
            <label className="block mb-1 font-medium text-sm text-[#1A1A1A]">
              Flow *
            </label>
            <select
              className="w-full border border-[#DAD9D9] rounded px-3 py-1.5 text-sm text-[#1A1A1A] bg-white"
              value={flowId}
              onChange={e => { setFlowId(e.target.value); setParams({}); }}
            >
              <option value="">Flow auswählen...</option>
              {availableFlows.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Modus-Toggle */}
        <div className="flex gap-2 mb-4">
          {(['interval', 'fixedTimes'] as const).map(mode => (
            <button
              key={mode}
              type="button"
              onClick={() => setScheduleMode(mode)}
              className={`px-3.5 py-1.5 rounded-md border text-xs font-medium transition-all ${
                scheduleMode === mode
                  ? 'bg-[#E9FF57] text-[#1A1A1A] border-[#E9FF57]'
                  : 'bg-white text-[#1A1A1A] border-[#DAD9D9]'
              }`}
            >
              {mode === 'interval' ? 'Intervall' : 'Feste Uhrzeiten'}
            </button>
          ))}
        </div>

        {scheduleMode === 'interval' ? (
          <>
            <div className="mb-4">
              <label className="block mb-1 font-medium text-sm text-[#1A1A1A]">
                Intervall *
              </label>
              <select
                className="border border-[#DAD9D9] rounded px-3 py-1.5 text-sm text-[#1A1A1A] bg-white"
                style={{ width: '180px' }}
                value={intervalMinutes}
                onChange={e => setIntervalMinutes(Number(e.target.value))}
              >
                {INTERVAL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-1 font-medium text-sm text-[#1A1A1A]">
                Laufzeit
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm">Von</span>
                <select className="border border-[#DAD9D9] rounded px-2 py-1 text-sm bg-white" style={{ width: '80px' }} value={activeFrom} onChange={e => setActiveFrom(Number(e.target.value))}>
                  {hourOptions.map(h => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                  ))}
                </select>
                <span className="text-sm">bis</span>
                <select className="border border-[#DAD9D9] rounded px-2 py-1 text-sm bg-white" style={{ width: '80px' }} value={activeTo} onChange={e => setActiveTo(Number(e.target.value))}>
                  {hourOptions.map(h => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                  ))}
                </select>
                {activeFrom !== activeTo && (
                  <span className="text-xs text-gray-400">
                    ({24 - (activeHoursToPause(activeFrom, activeTo)?.length || 0)}h aktiv)
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Gleiche Werte = rund um die Uhr. Unterstützt Nacht-Bereiche (z.B. 22:00–06:00).
              </p>
            </div>
          </>
        ) : (
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2">
              <label className="font-medium text-sm text-[#1A1A1A]">Anzahl Runs</label>
              <input
                className="border border-[#DAD9D9] rounded px-2 py-1 text-sm bg-white"
                type="number"
                min={1}
                max={24}
                value={customTimes.length}
                onChange={e => updateTimeCount(Number(e.target.value))}
                style={{ width: '70px' }}
              />
            </div>
            <div className="flex flex-wrap gap-2 bg-gray-50 rounded-md p-3">
              {customTimes.map((time, idx) => (
                <div key={idx} className="flex items-center gap-1">
                  <span className="text-xs text-gray-400 min-w-[1.25rem] text-right">
                    {idx + 1}.
                  </span>
                  <input
                    className="border border-[#DAD9D9] rounded px-2 py-1 text-sm bg-white"
                    type="time"
                    value={time}
                    onChange={e => updateTime(idx, e.target.value)}
                    style={{ width: '110px', fontSize: '0.85rem' }}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Der Flow wird an aktiven Wochentagen zu den angegebenen Uhrzeiten gestartet.
              Zeitzone: <span className="font-mono">Europe/Berlin</span> (Sommer-/Winterzeit-sicher).
            </p>
          </div>
        )}

        <div className="mb-4">
          <label className="block mb-1 font-medium text-sm text-[#1A1A1A]">
            Wochentage
          </label>
          <div className="flex gap-1.5">
            {DAY_ORDER.map(dayIdx => (
              <button
                key={dayIdx}
                type="button"
                onClick={() => toggleDay(dayIdx)}
                className={`px-2 py-1 rounded border text-xs font-medium transition-all ${
                  activeDays.includes(dayIdx)
                    ? 'bg-[#E9FF57] text-[#1A1A1A] border-[#E9FF57]'
                    : 'bg-white text-[#1A1A1A] border-[#DAD9D9]'
                }`}
              >
                {DAY_LABELS[dayIdx]}
              </button>
            ))}
          </div>
        </div>

        {paramDefs.length > 0 && (
          <div className="mb-4">
            <label className="block mb-2 font-medium text-sm text-[#1A1A1A]">
              Flow-Parameter
            </label>
            <div className="bg-gray-50 rounded-md p-3">
              {paramDefs.map(([key, def]: [string, any]) => (
                <div key={key} className="mb-3">
                  {def.type === 'boolean' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`sched-param-${key}`}
                        checked={params[key] === true || params[key] === 'true'}
                        onChange={e => setParams(p => ({ ...p, [key]: e.target.checked }))}
                      />
                      <label htmlFor={`sched-param-${key}`} className="font-medium text-xs text-[#1A1A1A]">
                        {def.label || key}
                      </label>
                    </div>
                  ) : (
                    <>
                      <label className="block mb-0.5 font-medium text-xs text-[#1A1A1A]">
                        {def.label || key}
                      </label>
                      <input
                        className="w-full border border-[#DAD9D9] rounded px-3 py-1.5 text-sm bg-white"
                        type={def.type === 'number' ? 'number' : 'text'}
                        placeholder={def.default != null ? String(def.default) : ''}
                        value={params[key] ?? ''}
                        onChange={e => setParams(p => ({ ...p, [key]: def.type === 'number' ? Number(e.target.value) : e.target.value }))}
                      />
                    </>
                  )}
                  {def.description && (
                    <p className="text-[0.7rem] text-gray-400 mt-0.5">{def.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mb-6">
          <input
            type="checkbox"
            id="schedule-enabled"
            checked={enabled}
            onChange={e => setEnabled(e.target.checked)}
          />
          <label htmlFor="schedule-enabled" className="font-medium text-sm text-[#1A1A1A]">
            Aktiviert
          </label>
        </div>

        <div className="flex gap-3 justify-end">
          <button
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#DAD9D9] rounded text-sm text-[#1A1A1A] hover:bg-gray-50"
            onClick={onClose}
            disabled={saving}
          >
            Abbrechen
          </button>
          <button
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#E9FF57] text-[#1A1A1A] rounded text-sm font-medium hover:bg-[#d4ea4d]"
            onClick={handleSave}
            disabled={saving || (!flowId && isCreate)}
          >
            {saving ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />}
            {saving ? 'Wird gespeichert...' : isCreate ? 'Erstellen' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================
// Gruppierung nach Programm (reine Anzeige — sortiert wie die Sidebar)
// =====================

interface ProgrammeMeta {
  id: string;
  name: string;
  category?: string;
  programmNr?: number;
  kategorieNr?: number;
}

interface ScheduleGroup {
  key: string;        // programmeId ('' = ohne Zuordnung)
  label: string;      // Programm-Anzeigename ("<nr>_<name>")
  programmNr?: number;
  category: string;
  items: FlowSchedule[];
}

/**
 * Gruppiert Schedules nach programmeId und sortiert die Gruppen wie die Sidebar
 * (categoryOrder, programOrder, Name). Schedules ohne Programm-Zuordnung kommen
 * als letzte Gruppe. Reine Anzeige-Logik — verändert keine Schedule-Daten.
 */
function groupSchedulesByProgramme(
  schedules: FlowSchedule[],
  programmes: ProgrammeMeta[],
): ScheduleGroup[] {
  const metaById = new Map(programmes.map(p => [p.id, p]));
  const byProg = new Map<string, FlowSchedule[]>();
  for (const s of schedules) {
    const key = s.programmeId || '';
    if (!byProg.has(key)) byProg.set(key, []);
    byProg.get(key)!.push(s);
  }

  const named = Array.from(byProg.entries())
    .filter(([key]) => key !== '')
    .map(([key, items]) => {
      const m = metaById.get(key);
      return {
        key,
        label: m ? formatProgrammeLabel(m) : key,
        programmNr: m?.programmNr,
        category: m ? formatCategoryLabel({ name: m.category ?? '', kategorieNr: m.kategorieNr }) : '',
        items,
        _sort: [m?.kategorieNr ?? 999, m?.programmNr ?? 999, m?.name || key] as [number, number, string],
      };
    })
    .sort((a, b) =>
      a._sort[0] - b._sort[0] ||
      a._sort[1] - b._sort[1] ||
      String(a._sort[2]).localeCompare(String(b._sort[2])),
    )
    .map(({ key, label, programmNr, category, items }) => ({ key, label, programmNr, category, items }));

  const orphans = byProg.get('') || [];
  if (orphans.length) {
    named.push({ key: '', label: 'Ohne Programm-Zuordnung', programmNr: undefined, category: 'manuell angelegt', items: orphans });
  }
  return named;
}

// =====================
// Schedules Tab
// =====================

const FlowSchedulesTab: React.FC<FlowSchedulesTabProps> = ({ flowCatalog }) => {
  const [schedules, setSchedules] = useState<FlowSchedule[]>([]);
  const [programmes, setProgrammes] = useState<ProgrammeMeta[]>([]);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [suche, setSuche] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editSchedule, setEditSchedule] = useState<FlowSchedule | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [res, progs] = await Promise.all([
        api.flows.getSchedules(),
        api.getProgrammes().catch(() => [] as ProgrammeMeta[]),
      ]);
      if (res.success) setSchedules(res.data);
      setProgrammes(progs as ProgrammeMeta[]);
    } catch {
      toast.error('Fehler beim Laden der Schedules');
    } finally {
      setLoading(false);
    }
  };

  const alleGruppen = groupSchedulesByProgramme(schedules, programmes);
  const groups = alleGruppen.filter((g) =>
    matchesProgrammeSearch({ name: g.label, programmNr: g.programmNr, id: g.key }, suche)
  );
  const expandAll = () =>
    setOpenGroups(Object.fromEntries(groups.map(g => [g.key, true])));
  const collapseAll = () => setOpenGroups({});
  const toggleGroup = (key: string) =>
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const handleToggle = async (schedule: FlowSchedule) => {
    try {
      const res = await api.flows.updateSchedule(schedule.flowId, {
        intervalMinutes: schedule.intervalMinutes,
        scheduleMode: schedule.scheduleMode || 'interval',
        customTimes: schedule.customTimes || null,
        enabled: !schedule.enabled,
      });
      if (res.success) {
        setSchedules(prev => prev.map(s =>
          s.flowId === schedule.flowId ? { ...s, enabled: !s.enabled } : s
        ));
        toast.success(`Schedule ${!schedule.enabled ? 'aktiviert' : 'deaktiviert'}`);
      }
    } catch {
      toast.error('Fehler beim Aktualisieren des Schedules');
    }
  };

  const handleSave = async (flowId: string, data: any) => {
    const res = await api.flows.updateSchedule(flowId, data);
    if (res.success) {
      toast.success(modalMode === 'create' ? 'Schedule erstellt' : 'Schedule gespeichert');
      setModalMode(null);
      setEditSchedule(null);
      await loadData();
    }
  };

  const handleDelete = async (schedule: FlowSchedule) => {
    if (!confirm(`Schedule für "${schedule.flowName || schedule.flowId}" wirklich löschen?`)) return;
    try {
      const res = await api.flows.deleteSchedule(schedule.flowId);
      if (res.success) {
        setSchedules(prev => prev.filter(s => s.flowId !== schedule.flowId));
        toast.success('Schedule gelöscht');
      }
    } catch {
      toast.error('Fehler beim Löschen des Schedules');
    }
  };

  // Tabelle einer Gruppe — Spalten + Aktionen identisch zur bisherigen Ansicht.
  const renderScheduleTable = (items: FlowSchedule[]) => (
    <div className="overflow-x-auto border-t border-[#DAD9D9]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#DAD9D9] bg-gray-50">
            <th className="text-left px-3 py-2 font-semibold text-gray-600">Schedule</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-600">Zeitplan</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-600">Aktiv</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-600">Laufzeit</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-600">Tage</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-600">Letzte Ausführung</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-600">Nächste Ausführung</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-600">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {items.map(schedule => (
            <tr key={schedule.flowId} className="border-b border-gray-100">
              <td className="px-3 py-2">
                <div className="font-medium text-[#1A1A1A]">{schedule.name || schedule.flowName || schedule.flowId}</div>
                {schedule.flowDescription && (
                  <div className="text-xs text-gray-500 mt-0.5">{schedule.flowDescription}</div>
                )}
                <div className="text-[11px] text-gray-400 mt-0.5 font-mono">{schedule.flowId}</div>
              </td>
              <td className="px-3 py-2">{formatScheduleType(schedule)}</td>
              <td className="px-3 py-2">
                <button
                  onClick={() => handleToggle(schedule)}
                  className="relative"
                  style={{
                    width: '44px', height: '24px', borderRadius: '9999px',
                    backgroundColor: schedule.enabled ? '#22c55e' : '#DAD9D9',
                    border: 'none', cursor: 'pointer', transition: 'background-color 0.2s',
                  }}
                  title={schedule.enabled ? 'Deaktivieren' : 'Aktivieren'}
                >
                  <span style={{
                    position: 'absolute', top: '3px',
                    left: schedule.enabled ? '22px' : '3px',
                    width: '18px', height: '18px', borderRadius: '50%',
                    backgroundColor: 'white', transition: 'left 0.2s',
                  }} />
                </button>
              </td>
              <td className="px-3 py-2 text-xs">{schedule.scheduleMode === 'fixedTimes' ? '—' : formatActiveHours(schedule.pauseHours)}</td>
              <td className="px-3 py-2 text-xs">{formatActiveDays(schedule.pauseDays)}</td>
              <td className="px-3 py-2">{formatDate(schedule.lastRunAt)}</td>
              <td className="px-3 py-2">{formatDate(schedule.nextRunAt)}</td>
              <td className="px-3 py-2">
                <div className="flex gap-1.5">
                  <button
                    className="inline-flex items-center px-2 py-1 border border-[#DAD9D9] rounded text-xs hover:bg-gray-50"
                    onClick={() => { setEditSchedule(schedule); setModalMode('edit'); }}
                    title="Bearbeiten"
                  >
                    <Edit3 size={14} />
                  </button>
                  <button
                    className="inline-flex items-center px-2 py-1 border border-[#DAD9D9] rounded text-xs text-red-500 hover:bg-gray-50"
                    onClick={() => handleDelete(schedule)}
                    title="Löschen"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A1A1A] mx-auto"></div>
        <span className="text-sm text-gray-500 mt-2 block">Lade Schedules...</span>
      </div>
    );
  }

  return (
    <div>
      {modalMode && (
        <ScheduleModal
          schedule={modalMode === 'edit' ? editSchedule : null}
          catalog={flowCatalog}
          existingFlowIds={schedules.map(s => s.flowId)}
          onClose={() => { setModalMode(null); setEditSchedule(null); }}
          onSave={handleSave}
        />
      )}

      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex gap-2">
          <button
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#DAD9D9] rounded text-xs font-medium hover:bg-gray-50 disabled:opacity-40"
            onClick={expandAll}
            disabled={groups.length === 0}
          >
            <ChevronDown size={15} />
            Alles aufklappen
          </button>
          <button
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#DAD9D9] rounded text-xs font-medium hover:bg-gray-50 disabled:opacity-40"
            onClick={collapseAll}
            disabled={groups.length === 0}
          >
            <ChevronRight size={15} />
            Alles zuklappen
          </button>
          <input
            type="text"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Programm suchen: Nummer oder Name…"
            className="border border-[#DAD9D9] rounded px-3 py-1.5 text-xs w-64 focus:outline-none focus:border-[#E9FF57]"
          />
        </div>
        <button
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#E9FF57] text-[#1A1A1A] rounded text-xs font-medium hover:bg-[#d4ea4d]"
          onClick={() => setModalMode('create')}
        >
          <Plus size={16} />
          Schedule erstellen
        </button>
      </div>

      {schedules.length === 0 ? (
        <div className="border border-[#DAD9D9] rounded-lg text-center p-8">
          <Calendar size={40} color="#9CA3AF" />
          <p className="mt-4 text-gray-500">Keine Schedules konfiguriert</p>
        </div>
      ) : (
        <div className="flex flex-col border border-[#DAD9D9] rounded-lg overflow-hidden bg-white divide-y divide-[#DAD9D9]">
          {groups.map(group => {
            const isOpen = !!openGroups[group.key];
            const activeCount = group.items.filter(s => s.enabled).length;
            const idleCount = group.items.length - activeCount;
            return (
              <div key={group.key || '__none__'}>
                <button
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50"
                  onClick={() => toggleGroup(group.key)}
                  aria-expanded={isOpen}
                >
                  {isOpen
                    ? <ChevronDown size={14} className="text-gray-500 shrink-0" />
                    : <ChevronRight size={14} className="text-gray-500 shrink-0" />}
                  <span className="text-sm font-semibold text-[#1A1A1A]">{group.label}</span>
                  {group.category && (
                    <span className="text-[10px] uppercase tracking-wide text-gray-400">{group.category}</span>
                  )}
                  <span className="ml-auto flex items-center gap-1.5">
                    {activeCount > 0 && (
                      <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-green-50 text-green-700">{activeCount} aktiv</span>
                    )}
                    {idleCount > 0 && (
                      <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{idleCount} aus</span>
                    )}
                  </span>
                </button>
                {isOpen && renderScheduleTable(group.items)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FlowSchedulesTab;
